import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { getProducts, getFacets } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Menu() {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState({ categories: [], brands: [], strains: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "All";
  const brand = params.get("brand") || "All";
  const strain = params.get("strain_type") || "All";
  const sort = params.get("sort") || "";
  const search = params.get("search") || "";

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === "All") next.delete(key);
    else next.set(key, val);
    setParams(next);
  };

  useEffect(() => { getFacets().then(setFacets).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    getProducts({
      category, brand, strain_type: strain, sort, search,
    }).then((d) => setProducts(d)).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [category, brand, strain, sort, search]);

  return (
    <main data-testid="menu-page" className="pt-28 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="mb-4 text-xs tracking-[0.2em] uppercase font-bold text-honey">Vertically Integrated Menu</div>
        <h1 className="font-display font-extrabold uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9] mb-10">
          The Dispensary
        </h1>

        {/* search + sort */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone" size={18} />
            <input
              data-testid="menu-search"
              value={search}
              onChange={(e) => update("search", e.target.value)}
              placeholder="Search strains, brands, categories…"
              className="w-full bg-ink2 border border-bone/10 focus:border-honey rounded-full pl-12 pr-6 py-4 text-bone placeholder-stone outline-none transition-colors"
            />
          </div>
          <div className="flex gap-4">
            <Select value={brand} onValueChange={(v) => update("brand", v)}>
              <SelectTrigger data-testid="menu-filter-brand" className="w-44 bg-ink2 border-bone/10 rounded-full h-auto py-4 text-bone">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent className="bg-ink2 border-bone/10 text-bone">
                <SelectItem value="All">All Brands</SelectItem>
                {facets.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort || "featured"} onValueChange={(v) => update("sort", v === "featured" ? "" : v)}>
              <SelectTrigger data-testid="menu-sort" className="w-44 bg-ink2 border-bone/10 rounded-full h-auto py-4 text-bone">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="bg-ink2 border-bone/10 text-bone">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="thc_desc">Highest THC</SelectItem>
                <SelectItem value="name">A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* category pills */}
        <div className="flex flex-wrap gap-3 mb-6">
          {["All", ...facets.categories].map((c) => (
            <button
              key={c}
              data-testid={`category-${c}`}
              onClick={() => update("category", c)}
              className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                category === c ? "bg-honey text-ink border-honey" : "border-bone/20 text-stone hover:border-honey hover:text-honey"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* strain pills */}
        <div className="flex flex-wrap gap-3 mb-12">
          {["All", ...facets.strains].map((s) => (
            <button
              key={s}
              data-testid={`strain-${s}`}
              onClick={() => update("strain_type", s)}
              className={`px-4 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition-colors ${
                strain === s ? "text-honey border border-honey" : "text-stone border border-transparent hover:text-honey"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="text-stone text-sm mb-6" data-testid="menu-count">{products.length} products</div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] bg-ink2 animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <p className="text-stone py-20 text-center">No products match your filters.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        )}
      </div>
    </main>
  );
}
